import { useEffect, useState } from 'preact/hooks';
import { api } from '../api';
import { serializePushSubscription, urlBase64ToUint8Array } from '../lib/push';

export function NotificationSettings() {
  const isSupported =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupported) return;

    let active = true;
    void (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (active) {
          setSubscribed(sub !== null);
          setPermission(Notification.permission);
        }
      } catch {
        // Service worker or push subscription check failure
      }
    })();

    return () => {
      active = false;
    };
  }, [isSupported]);

  if (!isSupported) {
    const isIos =
      typeof navigator !== 'undefined' &&
      /iphone|ipad|ipod/i.test(navigator.userAgent);

    return (
      <section class="section">
        <h2 class="section__title">Notifications</h2>
        <p class="muted">
          Push notifications are not supported by this browser.
          {isIos && (
            <>
              {' '}
              On iOS, notifications require adding Podcastie to your Home Screen
              via Safari's Share menu.
            </>
          )}
        </p>
      </section>
    );
  }

  const toggle = async (enable: boolean) => {
    setBusy(true);
    setError(null);

    try {
      const reg = await navigator.serviceWorker.ready;

      if (enable) {
        const perm = await Notification.requestPermission();
        setPermission(perm);

        if (perm !== 'granted') {
          setSubscribed(false);
          return;
        }

        const { publicKey } = await api.getVapidPublicKey();
        if (!publicKey) {
          throw new Error('Notification service is temporarily unavailable (no VAPID key configured).');
        }

        const applicationServerKey = urlBase64ToUint8Array(publicKey);
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });

        const payload = serializePushSubscription(sub, navigator.userAgent);
        await api.registerPushSubscription(payload);

        setSubscribed(true);
      } else {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          try {
            await api.unregisterPushSubscription(sub.endpoint);
          } catch {
            // Even if server unregister fails, unsubscribe locally
          }
          await sub.unsubscribe();
        }
        setSubscribed(false);
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to update notification settings.');
    } finally {
      setBusy(false);
    }
  };

  const isDenied = permission === 'denied';

  return (
    <section class="section">
      <h2 class="section__title">Notifications</h2>

      {error && <p class="error">{error}</p>}

      <label class="field">
        <span>Enable new episode notifications on this device</span>
        <input
          type="checkbox"
          checked={subscribed && !isDenied}
          disabled={busy || isDenied}
          onChange={(e) => void toggle((e.target as HTMLInputElement).checked)}
        />
      </label>

      {isDenied && (
        <p class="muted">
          Notifications are blocked in your browser settings. To enable notifications,
          please update your browser's site permissions for Podcastie.
        </p>
      )}
    </section>
  );
}
